<template>
<div class="container bg-white h-100 w-100">
    <div class="row h-100">

    <!-- Cargando -->
    <div v-if="isLoading" class="col-12 text-center my-4">
        <b-spinner label="Loading" />
    </div>

    <template v-if="!isLoading">

        <div v-if="loadError" class="col-12 text-center" style="padding-top: 20%;">
        <h4>{{ $t(`plane.${loadError}`) }}</h4>
        </div>

        <!-- Sin planos -->
        <div v-else-if="planes.length === 0" class="col-12 text-center" style="padding-top: 20%;">
        <h4>{{ $t("plane.no_floor_plant") }}</h4>
        </div>

        <!-- Con planos -->
        <div v-else class="col-12 p-1">
        <div class="border rounded p-1" style="background-color: #f2f2f2;">

            <!-- Tabs -->
            <ul class="nav nav-tabs">
            <li
                class="nav-item"
                v-for="(plane, index) in planes"
                :key="plane.id"
            >
                <button
                class="nav-link"
                :class="{ active: activeTab === index }"
                :title="plane.name.length > 15 ? plane.name : ''"
                @click="activeTab = index"
                >
                <span class="tab-name">{{ plane.name }}</span>
                </button>
            </li>
            </ul>

            <!-- Contenido tabs -->
            <div class="tab-content border rounded bg-white p-1">
            <div
                v-for="(plane, index) in planes"
                :key="plane.id"
                v-show="activeTab === index"
            >
                <div
                class="planeViewport"
                @wheel="onWheel($event, index)"
                @pointerdown="startPan($event, index)"
                @pointermove="movePan($event, index)"
                @pointerup="endPan(index)"
                @pointerleave="endPan(index)"
                @dragover.prevent
                @drop.prevent
                >

                <!-- Overlay para cerrar popover al click fuera -->
                <div
                    v-if="activeInfoDeviceId"
                    class="plane-overlay"
                    @click="activeInfoDeviceId = null"
                />

                    <div class="planeScene" :style="sceneTransformForIndex(index)">

                        <div v-if="!loadedImages[plane.id]" class="plane-skeleton">
                            <div class="plane-skeleton__shimmer" />
                        </div>

                        <img
                        :src="plane.path"
                        class="planeImage"
                        draggable="false"
                        @load="onImageLoad(plane.id)"
                        :style="{ opacity: loadedImages[plane.id] ? 1 : 0, transition: 'opacity 0.3s ease' }"
                        />

                        <Device
                            v-for="device in plane.devices"
                            :key="device.id"
                            :device="device"
                            :name="device.name"
                            :position="device.position"
                            :scale="getViewportState(index).scale"
                            :isCamera="!!device.position.cone.visible"
                            :activeInfoDeviceId="activeInfoDeviceId"
                            :showAlarm="device.showAlarm"
                            :alarmColor="device.alarmColor"
                            :showAlarmActions="device.can_update_alarm"
                            @toggle-info="activeInfoDeviceId = $event"
                            @actuator-info="onActuatorInfo"
                            @interphone-action="onInterphoneAction"
                            @alarm-action="refreshAlarms"
                            @update-alarm-style="onUpdateAlarmStyle"
                            @panel-updated="refreshPanelDevices"
                        />

                    </div>

                </div>
            </div>
            </div>

        </div>
        </div>

    </template>

    </div>

</div>
</template>

<script>
import router from '@/router'
import { ref, onMounted, computed, set } from "@vue/composition-api"
import { SLOT_TYPES, SOURCES } from "@/config/layoutConfig"
import layoutMixin from "@/mixins/layoutMixin"
import { usePlaneSocket } from "./composables/usePlaneSocket"
import { createDeviceRelations, normalizeFloorPlane } from './adapters'
import { PLANE_COLORS } from './config/presentation'
import { shouldShowDmpAlarm } from './composables/useDevicePresentation'
import store from "@/store"
import Device, { updateDeviceInCache } from "./Device.vue"

export default {
components: { Device },
mixins: [layoutMixin],
props: {
  data: {
    type: Object,
    default: null
  }
},
setup(props) {

    const hasExplicitUnitId = computed(() => {
        const slotData = props.data?.data ?? props.data

        return !!slotData && Object.prototype.hasOwnProperty.call(slotData, 'unit_id')
    })
    const id = computed(() => {
        if (hasExplicitUnitId.value) {
            return props.data?.data?.unit_id ?? props.data?.unit_id ?? null
        }

        return router.currentRoute.params.id
    })
    const planes    = ref([])
    const isLoading = ref(true)
    const loadError = ref(null)
    const loadedImages = ref({})
    const activeTab = ref(0)
    const activeInfoDeviceId = ref(null)

    const onImageLoad = (planeId) => {
        loadedImages.value = { ...loadedImages.value, [planeId]: true }
    }

    // ─── Zoom y pan independiente por plano ───────────────────────────────────
    const viewportStates = ref({})

    const getViewportState = (index) => {
    if (!viewportStates.value[index]) {
        set(viewportStates.value, index, {
        scale:      1,
        translateX: 0,
        translateY: 0,
        isPanning:  false,
        startX:     0,
        startY:     0,
        })
    }
    return viewportStates.value[index]
    }

    const sceneTransformForIndex = (index) => {
    const s = getViewportState(index)
    return {
        transform: `translate(${s.translateX}px, ${s.translateY}px) scale(${s.scale})`
    }
    }

    const onWheel = (e, index) => {
    e.preventDefault()
    const s           = getViewportState(index)
    const zoomIntensity = 0.001
    const delta       = -e.deltaY
    const newScale    = Math.min(Math.max(0.3, s.scale + delta * zoomIntensity), 4)
    const rect        = e.currentTarget.getBoundingClientRect()
    const mouseX      = e.clientX - rect.left
    const mouseY      = e.clientY - rect.top
    const scaleFactor = newScale / s.scale

    s.translateX = mouseX - scaleFactor * (mouseX - s.translateX)
    s.translateY = mouseY - scaleFactor * (mouseY - s.translateY)
    s.scale      = newScale
    }

    const startPan = (e, index) => {
        activeInfoDeviceId.value = null
    const s    = getViewportState(index)
    s.isPanning = true
    s.startX   = e.clientX - s.translateX
    s.startY   = e.clientY - s.translateY
    }

    const movePan = (e, index) => {
    const s = getViewportState(index)
    if (!s.isPanning) return
    s.translateX = e.clientX - s.startX
    s.translateY = e.clientY - s.startY
    }

    const endPan = (index) => {
    getViewportState(index).isPanning = false
    }

    // ─── WebSocket por plano ────────────────────────────────────────────────────── 
    const { connect: connectPlanes } = usePlaneSocket(
        // 1. device.updated normal
        (event, plane) => {
            const proxyId    = event.data?.id_camera_proxy
            const deviceData = event.data?.data
            if (!proxyId || !deviceData) return

            const presentation = deviceData.presentation ?? null
            const isDmpPayload = deviceData.provider === 'dmp' || presentation !== null
            const alarmColor = presentation?.pulse_color
                ?? deviceData.rule?.color
                ?? deviceData.color
                ?? PLANE_COLORS.offline
            const alarmIcon = presentation?.icon ?? deviceData.rule?.icon ?? null

            planes.value.forEach(p => {
                const device = p.devices.find(d => d.id_camera_proxy === proxyId)
                if (device) {
                    // Generic device events keep their previous behavior.
                    // DMP events receive their pulse decision from the backend.
                    device.showAlarm = isDmpPayload
                        ? presentation?.should_pulse === true
                        : true
                    device.alarmColor = alarmColor
                    device.alarmIcon  = alarmIcon
                    device.source_id  = deviceData.source_id ?? device.source_id
                }
            })
        },
        // 2. dispatched_alarm / canceled_alarm
        async (event) => {
            const eventType = event.event_type
            const typeId    = event.data?.type_id
            const cameraProxyId = event.data?.id_camera_proxy

            if (eventType === 'camera_alarm' && cameraProxyId) {
                planes.value.forEach(p => {
                    if (event.id_floor_plant && event.id_floor_plant !== p.id) return

                    p.devices.forEach(device => {
                        if (String(device.id_camera_proxy) !== String(cameraProxyId)) return

                        device.showAlarm = event.data?.active === true
                        if (event.data?.alarm_color) {
                            device.alarmColor = event.data.alarm_color
                        }
                    })
                })

                await refreshAlarms()
                return
            }

            if (!typeId) return

            const alarmId = `alarm-${typeId}`
            planes.value.forEach(p => {
                if (event.id_floor_plant && event.id_floor_plant !== p.id) return
                p.devices.forEach(device => {
                    if (device.id_generic === alarmId) {
                        device.active_alarm = eventType === 'dispatched_alarm'
                    }
                })
            })
            await refreshAlarms()
        },
        // 3. Alarm panel updates
        async (event) => {
            const eventType  = event.event_type

            if (eventType === 'dmp_panel_status_update') {
                const statusData = event.data
                if (!statusData?.panel_proxy_id || !statusData?.status_key) return

                const statusId = `dmp-status-${statusData.panel_proxy_id}-${statusData.status_key}`

                planes.value.forEach(plane => {
                    if (event.id_floor_plant && event.id_floor_plant !== plane.id) return

                    plane.devices.forEach(device => {
                        if (device.id_generic !== statusId) return

                        set(device, 'status', statusData.status)
                        set(device, 'value', statusData.value)
                        set(device, 'color', statusData.color)
                        set(device, 'color_icon', statusData.color_icon)
                        set(device, 'should_pulse', statusData.should_pulse === true)
                        set(device, 'showAlarm', statusData.should_pulse === true)
                    })
                })

                return
            }

            const deviceData = event.data?.data
            const proxyId    = event.data?.id_camera_proxy
            const isDmpEvent = ['dmp_panel_device_alarm', 'dmp_panel_update'].includes(eventType)

            if (proxyId && deviceData) {
                if (isDmpEvent) {
                    updateDeviceInCache(event)
                }

                planes.value.forEach(p => {
                    if (event.id_floor_plant && event.id_floor_plant !== p.id) return
                    p.devices.forEach(device => {
                        if (!matchesPanelDevice(device, {
                            idCameraProxy: proxyId,
                            sourceId: deviceData.source_id,
                            name: deviceData.name,
                        })) return

                        // Update the placed device immediately; panel data is only a later snapshot.
                        set(device, 'status', deviceData.status || device.status)
                        set(device, 'source_id', deviceData.source_id || device.source_id)

                        // The event type is authoritative when a floor device was initially
                        // normalized without its DMP vendor metadata.
                        if (isDmpEvent) {
                            set(device, 'vendor', 'dmp')
                        }

                        if (device.vendor === 'bosch' || isDmpEvent) {
                            set(device, 'latest_alarm', {
                                ...(device.latest_alarm || {}),
                                ...deviceData,
                            })
                        }
                        if (isDmpEvent) {
                            applyDmpPresentation(device, deviceData)
                        }
                        if (device.vendor === 'bosch') {
                            set(device, 'bypass_text', deviceData.bypass_text || '')
                            set(device, 'visual_label', deviceData.visual_label || deviceData.status || '')
                            set(device, 'status_color', deviceData.status_color || deviceData.color || null)
                            set(device, 'alarmColor', deviceData.status_color || deviceData.color || device.alarmColor)
                        }

                        if (deviceData.virtual_bypass !== undefined) {
                            set(device, 'virtual_bypass', !!deviceData.virtual_bypass)
                        }

                        if (eventType === 'bosch_panel_device_alarm') {
                            set(device, 'showAlarm', deviceData.should_pulse === true)
                        } else if (eventType === 'bosch_panel_update') {
                            set(device, 'showAlarm', deviceData.should_pulse === true)
                        }
                    })
                })
            }

            // DMP payloads are device updates, not panel snapshots.
            const panelProxyId = event.data?.panel_proxy_id || (isDmpEvent ? null : proxyId)
            if (panelProxyId) {
                delete panelInfoCache[panelProxyId]
                const raw = await loadPanelInfo(panelProxyId)
                if (raw) {
                    planes.value.forEach(plane => {
                        if (event.id_floor_plant && event.id_floor_plant !== plane.id) return
                        plane.devices.forEach(device => {
                            if (String(device.panel_proxy_id) !== String(panelProxyId) &&
                                String(device.id_camera_proxy) !== String(panelProxyId)) return
                            const latestAlarm = findLatestAlarmInRaw(raw, device)
                            if (latestAlarm) {
                                set(device, 'latest_alarm', latestAlarm)
                                if (isDmpDevice(device, latestAlarm)) {
                                    applyDmpPresentation(device, latestAlarm)
                                }
                            }
                        })
                    })
                }
            }
        }
    )
    // ─── Carga de planos ──────────────────────────────────────────────────────
    const loadPlanes = async () => {
    isLoading.value = true
    loadError.value = null

    if (!id.value) {
        loadError.value = 'floor_plant_context_missing'
        isLoading.value = false
        return
    }

    let floorRes
    try {
        floorRes = await store.dispatch("unit/getFloorPlantAll", { id: id.value, withMaintenance: true })
    } catch (err) {
        loadError.value = err?.response?.status === 403
            ? 'no_floor_plant_permission'
            : 'floor_plant_load_error'
        console.error('Error al cargar planos:', err)
        isLoading.value = false
        return
    }

    let relationsRes = { data: {} }
    try {
        relationsRes = await store.dispatch("unit/getWithRelations", {
            id: id.value,
            params: { withCameras: false, withDevices: true, view: 'grid' }
        })
    } catch (err) {
        console.error('Error al cargar relaciones de la instalación:', err)
    }

    try {

        const relations = createDeviceRelations(relationsRes)
        const sortedPlanes = [...(floorRes.data ?? [])]
            .sort((first, second) => (first.order ?? 0) - (second.order ?? 0))

        planes.value = sortedPlanes.map((plane, index) => {
            set(viewportStates.value, index, {
                scale: parseFloat(plane.scale) || 1,
                translateX: parseFloat(plane.translate_x) || 0,
                translateY: parseFloat(plane.translate_y) || 0,
                isPanning: false,
                startX: 0,
                startY: 0,
            })

            return normalizeFloorPlane(plane, relations)
        })

    } catch (err) {
        loadError.value = 'floor_plant_load_error'
        console.error('Error al preparar planos:', err)
    }
    // isLoading.value = false
    }

    //Esta funcion la vamos a utilizar para actualizar los datos de las alarmas cada action que ejecutamos
    const refreshAlarms = async () => {
    try {
        const relationsRes = await store.dispatch("unit/getWithRelations", {
        id: id.value,
        params: { withCameras: false, withDevices: true, view: 'grid' }
        })

        const relations = createDeviceRelations(relationsRes)

        // Actualizar los devices de tipo alarm en todos los planos
        planes.value.forEach(plane => {
        plane.devices.forEach(device => {
            if (device.device_type === 'alarm' && device.id_generic) {
            const alarmData = relations.alarms[device.id_generic]
            if (alarmData) {
                device.active_alarm     = alarmData.active_alarm
                device.can_update_alarm = alarmData.can_update_alarm
                device.dispatch_url     = alarmData.dispatch_url
                device.cancel_url       = alarmData.cancel_url
            }
            }

            if (device.device_type === 'camera') {
                const cameraAlarm = relations.cameraAlarmStates[device.id_camera_proxy]
                device.showAlarm = cameraAlarm?.active === true
                if (cameraAlarm?.alarm_color) {
                    device.alarmColor = cameraAlarm.alarm_color
                }
            }
        })
        })
    } catch (err) {
        console.error('Error al refrescar alarmas:', err)
    }
    }

    const onUpdateAlarmStyle = ({ device, latestAlarm, alarmColor, alarmIcon, showAlarm }) => {
        planes.value.forEach(p => {
            const d = p.devices.find(dev => dev.id_camera_proxy === device.id_camera_proxy)
            if (d) {
            if (latestAlarm) set(d, 'latest_alarm', latestAlarm)
            set(d, 'alarmColor', alarmColor)
            set(d, 'alarmIcon', alarmIcon)
            set(d, 'showAlarm', showAlarm)
            }
        })
    }

    // Cache global en index.vue — keyed por panel_proxy_id
    const panelInfoCache = {}

    const loadPanelInfo = async (panelProxyId) => {
    if (!panelProxyId) return null
    
    // Si ya está en cache retornar inmediatamente
    if (panelInfoCache[panelProxyId]) return panelInfoCache[panelProxyId]

    const response = await store.dispatch("alarm_panels/getInfo", {
        device_proxy_id: panelProxyId,
        withDevices:     true,
    })
    // Do not read the shared Vuex item here: several panel requests can finish
    // in a different order and overwrite it before this panel is cached.
    const raw = response?.data?.devices || {}
    panelInfoCache[panelProxyId] = raw
    return raw
    }

    const refreshPanelDevices = async ({ panelProxyId }) => {
    if (!panelProxyId) return

    delete panelInfoCache[panelProxyId]
    const raw = await loadPanelInfo(panelProxyId)
    if (!raw) return

    // The panel snapshot is authoritative after an operator command.
    planes.value.forEach(plane => {
        plane.devices.forEach(device => {
            const belongsToPanel = String(device.panel_proxy_id) === String(panelProxyId)
                || String(device.id_camera_proxy) === String(panelProxyId)
            if (!belongsToPanel) return

            const panelDevice = String(device.id_camera_proxy) === String(panelProxyId)
                ? raw.panel
                : findDeviceInPanelRaw(raw, device)
            if (!panelDevice) return

            set(device, 'status', panelDevice.status || device.status)
            set(device, 'bypass_text', panelDevice.bypass_text || '')
            set(device, 'visual_label', panelDevice.visual_label || panelDevice.status || device.visual_label)
            set(device, 'status_color', panelDevice.status_color || null)
            set(device, 'showAlarm', panelDevice.should_pulse === true)
        })
    })
    }

    // Llamar al montar para todos los devices del plano
    const loadAllDeviceInfo = async () => {
    // Agrupar devices por panel_proxy_id para hacer UNA sola request por panel
    const panelProxyIds = new Set()
    planes.value.forEach(plane => {
        plane.devices.forEach(device => {
        if (device.panel_proxy_id) panelProxyIds.add(device.panel_proxy_id)
        else if (device.device_type === 'alarm_bridge') panelProxyIds.add(device.id_camera_proxy)
        })
    })

    // Cargar info de cada panel UNA sola vez
    await Promise.all([...panelProxyIds].map(id => loadPanelInfo(id)))

    // Actualizar cada device con su latest_alarm
    planes.value.forEach(plane => {
        plane.devices.forEach(device => {
            const panelId = device.panel_proxy_id || 
                            (device.device_type === 'alarm_bridge' ? device.id_camera_proxy : null)
            if (!panelId) return

            const raw = panelInfoCache[panelId]
            if (!raw) return

            const latestAlarm = findLatestAlarmInRaw(raw, device)
            const panelDevice = findDeviceInPanelRaw(raw, device)
            if (panelDevice) {
                set(device, 'virtual_bypass', !!panelDevice.virtual_bypass)
            }
            if (latestAlarm) {
                set(device, 'latest_alarm', latestAlarm) 
                if (isDmpDevice(device, latestAlarm)) {
                    applyDmpPresentation(device, latestAlarm)
                }
            }
        })
    })
    }

    const findLatestAlarmInRaw = (raw, identity) => {
    const device = findDeviceInPanelRaw(raw, identity)
    return device?.latest_alarm && !Array.isArray(device.latest_alarm)
        ? device.latest_alarm
        : null
    }

    /**
     * Applies the DMP visual contract emitted by the backend to a placed icon.
     * The legacy branch only supports a server that has not deployed it yet.
     */
    const applyDmpPresentation = (device, latestAlarm) => {
    const presentation = latestAlarm?.presentation

    if (presentation) {
        set(device, 'showAlarm', presentation.should_pulse === true)
        set(device, 'alarmIcon', presentation.icon ?? null)
        set(device, 'alarmColor', presentation.pulse_color ?? PLANE_COLORS.alarm)
        return
    }

    const tag = latestAlarm?.tag?.icon ? latestAlarm.tag : null
    const rule = latestAlarm?.rule ?? null
    const isAlarmed = shouldShowDmpAlarm(latestAlarm)

    set(device, 'showAlarm', isAlarmed)
    set(device, 'alarmIcon', (isAlarmed && rule?.icon) || tag?.icon || device.alarmIcon || null)
    set(device, 'alarmColor', rule?.color || tag?.icon_color || latestAlarm?.color || PLANE_COLORS.alarm)
    }

    /** Identifies DMP payloads without relying on the proxy type of old plans. */
    const isDmpDevice = (device, latestAlarm) => (
    device?.vendor === 'dmp'
        || latestAlarm?.provider === 'dmp'
        || latestAlarm?.presentation !== undefined
    )

    const matchesPanelDevice = (device, { idCameraProxy, sourceId, name }) => {
    const matches = (value, expected) => (
        value !== null
        && value !== undefined
        && expected !== null
        && expected !== undefined
        && String(value) === String(expected)
    )

    return matches(device.id_camera_proxy ?? device.device_proxy_id, idCameraProxy)
        || matches(device.source_id, sourceId)
        || matches(device.name ?? device.text, name)
    }

    const matchesPanelSnapshotDevice = (item, identity) => {
    const idCameraProxy = identity?.id_camera_proxy ?? identity?.idCameraProxy ?? identity
    const sourceId = identity?.source_id ?? identity?.sourceId ?? null
    const name = identity?.name ?? null

    return matchesPanelDevice(item, { idCameraProxy, sourceId, name })
        || matchesPanelDevice(item.latest_alarm ?? {}, { idCameraProxy, sourceId, name })
        || (sourceId !== null && sourceId !== undefined && String(item.id) === String(sourceId))
    }

    const findDeviceInPanelRaw = (raw, identity) => {
    if (!raw || !identity) return null

    for (const key in raw) {
        if (key === 'panel' || !Array.isArray(raw[key])) continue

        for (const item of raw[key]) {
        if (matchesPanelSnapshotDevice(item, identity)) return item

        const subDevice = item.devices?.find(sub =>
            matchesPanelSnapshotDevice(sub, identity)
        )
        if (subDevice) return subDevice
        }
    }

    return null
    }

    onMounted(async () => {
        try {
            await loadPlanes()
            await loadAllDeviceInfo() 
        } catch (err) {
            console.error('Error en carga inicial:', err)
        } finally {
            isLoading.value = false    
            connectPlanes(planes.value)
        }
    })

    return {
    id,
    planes,
    isLoading,
    loadError,
    activeTab,
    viewportStates,
    getViewportState,
    sceneTransformForIndex,
    onWheel,
    startPan,
    movePan,
    endPan,
    activeInfoDeviceId,
    loadedImages,
    onImageLoad,
    refreshAlarms,
    onUpdateAlarmStyle,
    refreshPanelDevices,
    }
    
},
methods: {
    onActuatorInfo({ device }) {
        const urlActuators = `${window.__env.VUE_APP_BASE_URL}/v1/services/actuators/info/`
        this.setSlotData(
        SLOT_TYPES.INFO,
        device.name,
        { data: { url: urlActuators + device.id_camera_proxy } },
        SOURCES.ACTUATOR
        )
    },
    onInterphoneAction({ device }) {
        this.setSlotData(
            SLOT_TYPES.INTERPHONE,
            device.id_camera_proxy,
            { data: device }
        )
    }
}

}
</script>

<style lang="scss" scoped>
.planeViewport {
width: 100%;
height: 70vh;
overflow: hidden;
position: relative;
cursor: grab;
}


.planeViewport:active {
cursor: grabbing;
}

.planeScene {
position: absolute;
transform-origin: top left;
overflow: visible;
}

.planeImage {
display: block;
user-select: none;
pointer-events: none;
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────
.nav-tabs {
border-bottom: none;
gap: 4px;
padding: 6px 6px 0 6px;
}

.nav-tabs .nav-item .nav-link {
border: none;
border-radius: 8px 8px 0 0;
padding: 6px 14px;
font-size: 13px;
font-weight: 500;
color: #385d7b;
background: rgba(255, 255, 255, 0.4);
transition: all 0.2s ease;

&:hover {
    background: rgba(255, 255, 255, 0.7);
    color: #495057;
}

&.active {
    background: #385d7b;
    color: white;
    font-weight: 600;
}
}

.tab-name {
display: inline-block;
max-width: 120px;
overflow: hidden;
text-overflow: ellipsis;
white-space: nowrap;
vertical-align: middle;
}

.plane-skeleton {
  position: absolute;
  width: 100%;
  height: 100%;
  min-width: 400px;
  min-height: 300px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.plane-skeleton__shimmer {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255,255,255,0)    0%,
    rgba(255,255,255,0.6)  50%,
    rgba(255,255,255,0)    100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
}

.plane-overlay {
  position: fixed;
  inset: 0;
  z-index: 99;
  background: transparent;
  cursor: default;
}
</style>
